import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {config} from 'config/env';
import i18n from 'config/i18n/i18n';
import {PROMOTION_SUGGESTION_TARGET_UNIT} from 'constants/recommendation';
import {useAppSelector} from 'hooks';
import {Product, ProductSearchResult} from 'types';
import {CollectionType} from 'types/recommedation-types/recommendations';
import {formatPriceWithoutDecimal} from 'utils';
import {getProPointPromotions} from 'utils/product/getProPointPromotions';
import {ProductModel} from 'utils/product/types';
import {ComplexPromotionType, PROPOINT_COLLECTION} from 'utils/recommendation-utils/proPointsPromotion.util';

export const RIBBONS_TITLE = {
  POINT: 'point',
  LOYALTY: 'loyalty'
};

export const usePromotionRibbon = (product: Product | ProductSearchResult) => {
  const {complexPromotion} = useAppSelector((state) => state.recommendations);
  const isComplexPriority = config.RECOM_LOYALTY_COMPLEX_PROMOTION_PRIORITY;

  const {t} = useTranslation();
  const locale = i18n.language;
  const isThai = locale === 'th';
  const {promotionSuggestion, proPointPromotionsApplicable, totalPromotions} = useMemo(() => {
    return getProPointPromotions(product as unknown as ProductModel);
  }, [product]);

  let title = '';
  let type = PROPOINT_COLLECTION;

  const promotion = (complexPromotion && complexPromotion[product.sku]) ||
    (complexPromotion && complexPromotion[product?.makroId || product.id]) || {
      promoAdvice: [],
      loading: false
    };

  const complexPrmotions3P = [
    ...(product.variants?.flatMap((variant) =>
      (variant.complexPromotion3P || []).map((promotion: {title: any; startDate: string; endDate: string}) => {
        return {
          type: ComplexPromotionType.SELLER_PROMO,
          title: promotion.title,
          startDate: promotion.startDate as string,
          endDate: promotion.endDate as string,
          skuCount: 0,
          terms: []
        };
      })
    ) || [])
  ];

  const isComplexPromotionEnabled = config.IS_COMPLEX_PROMOTION_ENABLED;
  let hasComplexPromotion = false;

  const hasProPointPromotion = product.collections?.some((p) => p.type === CollectionType.PROPOINT_PROMOTION);

  if (promotion && isComplexPromotionEnabled) {
    hasComplexPromotion = promotion.promoAdvice.length > 0 || complexPrmotions3P.length > 0;
  }

  const isPlpProPointPromotionEnabled = proPointPromotionsApplicable && config.RECOM_PLP_PROPOINT_PROMOTION_ENABLED;
  const isAsPlpProPointPromotionEnabled =
    proPointPromotionsApplicable && config.RECOM_AS_PLP_PROPOINT_PROMOTION_ENABLED;
  const isAsComplexPromotionEnabled = hasComplexPromotion && config.RECOM_AS_PLP_PROPOINT_PROMOTION_ENABLED;

  // Set title and type based on promotion type
  const setPromotionTitleAndType = () => {
    if (promotion.promoAdvice.length > 0) {
      title = promotion.promoAdvice[0].title;
      type = promotion.promoAdvice[0].type;
    } else if (complexPrmotions3P.length > 0) {
      title = complexPrmotions3P[0].title;
      type = complexPrmotions3P[0].type;
    }
  };

  if (proPointPromotionsApplicable) {
    const suggestion = promotionSuggestion as {
      targetType: string;
      rewardType: string;
      targetValue: number;
      rewardValue: number;
    };
    const promotionTextKey =
      suggestion.targetType === PROMOTION_SUGGESTION_TARGET_UNIT
        ? 'labels.proPointSuggestionUnits'
        : 'labels.proPointSuggestion';

    title = t(promotionTextKey, {
      targetValue: formatPriceWithoutDecimal(promotionSuggestion.targetValue),
      rewardValue: promotionSuggestion.rewardValue
    });
  } else if (hasComplexPromotion) {
    setPromotionTitleAndType();
  }

  if (isComplexPriority && hasComplexPromotion) {
    setPromotionTitleAndType();
  }

  const displayPromotion = totalPromotions + promotion.promoAdvice.length + (complexPrmotions3P?.length || 0);

  let displayRibbon = !promotion.loading;
  const promotionCollectionCount =
    product.collections?.filter((c) => {
      return c.type === CollectionType.PROMOTION;
    }).length || 0;
  const isPointTitle = product?.collections?.some((p) => {
    if (p.type !== CollectionType.PROMOTION) {
      return false;
    }

    const enTitle = isThai ? p.titleAlt : p.title;

    return enTitle?.toLowerCase().includes(RIBBONS_TITLE.POINT);
  });
  if (promotionCollectionCount) {
    //has manual promotion check condition
    //case 1 has automate and complex
    if (hasProPointPromotion && hasComplexPromotion) {
      displayRibbon = false;
    }

    //case 2 has suggestion don't have complex
    if (hasProPointPromotion && !hasComplexPromotion) {
      //check if it has point title if yes we need to hide it
      if (isPointTitle) {
        displayRibbon = false;
      } else {
        displayRibbon = true;
      }
    }

    //case 3 has complex don't have suggestion
    if (!hasProPointPromotion && hasComplexPromotion) {
      //show if contain point
      if (isPointTitle) {
        displayRibbon = true;
      } else {
        displayRibbon = false;
      }
    }

    //case 4 has don't have any
    if (!hasProPointPromotion && !hasComplexPromotion) {
      displayRibbon = true;
    }
  }

  return {
    title,
    totalPromotions: displayPromotion,
    complexPromotionLoading: promotion?.loading,
    isPlpProPointPromotionEnabled,
    isAsPlpProPointPromotionEnabled,
    type,
    hasComplexPromotion,
    isAsComplexPromotionEnabled,
    displayRibbon
  };
};
